import { Label, Radio, RadioGroup } from "@headlessui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat, PatternFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { Button } from "../../Components/Button";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { useAuthState } from "../../Context";
import {
  requestGetNPWPStatus,
  requestListNPWPUsage,
} from "../../Context/npwp/actions";
import { useNPWPDispatch, useNPWPState } from "../../Context/npwp/context";
import { classNames, getApiErrorBody } from "../../Utils";
import Partner from "../../Components/Partner";
import MaintenanceModal from "../../Components/MaintenanceModal";
import Badge from "../../Components/Badge";
import { useRemoteConfig } from "../../Context/firebase/context";

const quota = 2;

const validationOptions = [
  { name: "NPWP", value: "npwp" },
  { name: "NIK", value: "nik" },
];

function NPWPValidate(props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useNPWPDispatch();
  const {
    usage,
    usageLoading,
    loading,
    npwp: npwpResult,
    error,
  } = useNPWPState();

  const { user, getUserLoading } = useAuthState();

  const { npwp_validation: validationConfig, loading: configLoading } =
    useRemoteConfig();

  const [npwp, setnpwp] = useState("");
  const [nik, setnik] = useState("");
  const [validationType, setvalidationType] = useState(validationOptions[0]);
  const [validationEnabled, setvalidationEnabled] = useState(true);
  const [validationStatus, setvalidationStatus] = useState("");
  const [validationMaintenanceVisible, setvalidationMaintenanceVisible] =
    useState(false);

  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });

  const [premiumModal, setPremiumModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });

  useEffect(() => {
    if (user.membership === "PREMIUM") {
      requestListNPWPUsage(dispatch, { userID: user.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    if (!error) {
    } else {
      let errorMessage = "Error checking NPWP.";
      const errorResponseBody = getApiErrorBody(error);

      if (errorResponseBody?.message) {
        errorMessage = errorResponseBody?.message;
      }
      if (errorResponseBody?.errors) {
        errorMessage = "";
        for (let i = 0; i < errorResponseBody.errors.length; i++) {
          if (errorMessage !== "") {
            errorMessage += ` ${errorResponseBody.errors[i]}`;
          } else {
            errorMessage += errorResponseBody.errors[i];
          }
        }
      }
      setModal({
        visible: true,
        title: t("Error"),
        description: t(errorMessage),
        type: "ALERT",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const onValidateNPWP = () => {
    if (!validationEnabled) {
      setvalidationMaintenanceVisible(true);
    } else {
      if (user.membership !== "PREMIUM") {
        setPremiumModal({
          visible: true,
          title: t("Premium Feature"),
          description: t(
            "Only premium member can use the validate NPWP feature."
          ),
          type: "WARNING",
        });
      } else {
        if (validationType.value === "nik") {
          requestGetNPWPStatus(dispatch, { nik });
        }
        if (validationType.value === "npwp") {
          requestGetNPWPStatus(dispatch, { npwp });
        }
      }
    }
  };

  const onUpgradeToPremium = () => {
    if (!validationEnabled) {
      setvalidationMaintenanceVisible(true);
    } else {
      navigate("/subscription");
    }
  };

  const renderMaintenanceBadge = () => {
    const isUnavailable =
      validationStatus === "OFFLINE" ||
      validationStatus === "SCHEDULED_OFFLINE" ||
      validationStatus === "SCHEDULED_ACTIVE";

    const badgeValue = isUnavailable ? "unavailable" : "maintenance";
    const badgeStyle = isUnavailable ? "secondary" : "alert";

    return (
      <div className="flex items-center">
        <Badge title={badgeValue} type={badgeStyle} />
      </div>
    );
  };

  return (
    <div className="max-w-lg">
      <ModalConfirm
        open={modal.visible}
        setOpen={(open) => {
          setModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={modal.title}
        description={modal.description}
        type={modal.type}
      />
      <ModalConfirm
        open={premiumModal.visible}
        setOpen={(open) => {
          setPremiumModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={premiumModal.title}
        description={premiumModal.description}
        type={premiumModal.type}
        confirmTitle={t("Subscribe")}
        onConfirm={() => {
          navigate("/subscription");
        }}
      />
      <MaintenanceModal
        isVisible={validationMaintenanceVisible}
        onClose={() => setvalidationMaintenanceVisible(false)}
        onChangeAvailability={(available) => setvalidationEnabled(available)}
        onChangeStatus={(status) => setvalidationStatus(status)}
        configValue={validationConfig}
      />
      <div className="bg-white shadow rounded-lg">
        <div className="p-4 grid gap-4">
          <div>
            <label
              htmlFor="type"
              className="block text-sm font-medium text-gray-700"
            >
              {t("Type of Validation")}
            </label>
            <RadioGroup
              value={validationType}
              onChange={(value) => {
                if (value.value === "npwp") {
                  setnik("");
                }
                if (value.value === "nik") {
                  setnpwp("");
                }
                setvalidationType(value);
              }}
              className="mt-2"
            >
              <Label className="sr-only">{t("Choose validation type")}</Label>
              <div className="grid grid-cols-2 gap-2">
                {validationOptions.map((option) => (
                  <Radio
                    key={option.name}
                    value={option}
                    className={({ checked }) =>
                      classNames(
                        loading
                          ? "opacity-25 cursor-not-allowed"
                          : "cursor-pointer focus:outline-none data-[focus]:ring-2 data-[focus]:ring-blue-600 data-[focus]:ring-offset-2",
                        checked
                          ? "bg-blue-600 border-transparent text-white hover:bg-blue-700"
                          : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50",
                        "border rounded-md py-2 px-2 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                      )
                    }
                    disabled={loading}
                  >
                    <Label as="p">{option.name}</Label>
                  </Radio>
                ))}
              </div>
            </RadioGroup>
          </div>
          <div>
            <label
              htmlFor="npwp"
              className="block text-sm font-medium text-gray-700"
            >
              {t("Tax Identification Number (NPWP)")}
            </label>
            <NumericFormat
              type="text"
              name="npwp"
              id="npwp"
              allowLeadingZeros
              disabled={validationType.value === "nik" || loading}
              className={classNames(
                "mt-1 py-2 px-3 ebilling-input text-sm",
                validationType.value === "nik" || loading ? "bg-gray-100" : ""
              )}
              placeholder={t("Enter the tax identification number (NPWP)")}
              displayType="input"
              value={npwp}
              onValueChange={(value) => {
                setnpwp(value.value);
              }}
              isAllowed={({ value }) => value.length <= 16}
            />
          </div>
          <div>
            <label
              htmlFor="nik"
              className="block text-sm font-medium text-gray-700"
            >
              {t("National ID (NIK)")}
            </label>
            <PatternFormat
              type="text"
              name="nik"
              id="nik"
              disabled={validationType.value === "npwp" || loading}
              className={classNames(
                "mt-1 py-2 px-3 ebilling-input text-sm",
                validationType.value === "npwp" || loading ? "bg-gray-100" : ""
              )}
              placeholder={t("Enter the national id number (NIK)")}
              displayType="input"
              format="################"
              mask="_"
              value={nik}
              onValueChange={(value) => {
                setnik(value.value);
              }}
            />
          </div>
          {npwpResult.nama && (
            <div className="bg-white shadow rounded-lg h-44 relative text-white">
              <img
                alt="background"
                className="relative object-cover w-full h-full rounded-lg"
                src="https://pajakind-images.s3.ap-southeast-1.amazonaws.com/npwp-bg.png"
              />
              <div className="w-full px-4 absolute top-4">
                <div className="flex justify-between">
                  <div>
                    <p className="font-light text-xs">{t("Name")}</p>
                    {loading ? (
                      <div className="animate-pulse h-4 mt-[4px] mb-[4px] w-32 bg-gray-300 rounded-md"></div>
                    ) : (
                      <p className="font-medium">{npwpResult.nama}</p>
                    )}
                  </div>
                  <img
                    alt="npwp-logo"
                    className="h-8"
                    src="https://pajakind-images.s3.ap-southeast-1.amazonaws.com/npwp-logo.png"
                  />
                </div>
                {npwpResult.npwp && (
                  <div className="pt-4">
                    <p className="font-light text-xs">{t("NPWP")}</p>
                    {loading ? (
                      <div className="animate-pulse h-4 mt-[4px] mb-[4px] w-32 bg-gray-300 rounded-md"></div>
                    ) : (
                      <NumericFormat
                        className="font-medium"
                        displayType={"text"}
                        value={npwpResult.npwp}
                      />
                    )}
                  </div>
                )}
                {!npwpResult.npwp && npwpResult.nik && (
                  <div className="pt-4">
                    <p className="font-light text-xs">{t("NIK")}</p>
                    {loading ? (
                      <div className="animate-pulse h-4 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
                    ) : (
                      <p className="font-medium">{npwpResult.nik}</p>
                    )}
                  </div>
                )}
                <div className="pt-4">
                  <div className="flex justify-between">
                    <div className="">
                      <p className="font-light text-xs">{t("NPWP Status")}</p>
                      {loading ? (
                        <div className="animate-pulse h-3 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
                      ) : (
                        <p className="font-medium text-sm">
                          {npwpResult.statusWp}
                        </p>
                      )}
                    </div>
                    <div className="">
                      <p className="font-light text-xs">
                        {t("Filling Status")}
                      </p>
                      {loading ? (
                        <div className="animate-pulse h-3 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
                      ) : (
                        <p className="font-medium text-sm">
                          {npwpResult.statusSpt}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 flex justify-between items-center bg-gray-50 rounded-b-lg">
          {user.membership === "PREMIUM" ? (
            <div>
              <p className="text-xs text-slate-500">{t("Today's Quota")}</p>
              {usageLoading || configLoading ? (
                <div className="animate-pulse h-3 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
              ) : !validationEnabled ? (
                renderMaintenanceBadge()
              ) : (
                <p className="text-xs font-medium">
                  {quota - usage} {t("Validation")}
                </p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-xs text-slate-500">{t("Membership")}</p>
              {getUserLoading || configLoading ? (
                <div className="animate-pulse h-3 mt-[4px] mb-[4px] w-16 bg-gray-300 rounded-md"></div>
              ) : !validationEnabled ? (
                renderMaintenanceBadge()
              ) : (
                <p className="text-xs font-medium">
                  {t("Not Enrolled to Premium")}
                </p>
              )}
            </div>
          )}
          {user.membership === "PREMIUM" ? (
            <Button
              onClick={onValidateNPWP}
              loading={loading}
              disabled={
                getUserLoading ||
                loading ||
                configLoading ||
                (validationType.value === "npwp" &&
                  (npwp.length < 15 || npwp.length > 16)) ||
                (validationType.value === "nik" && nik.length !== 16) ||
                quota - usage <= 0
              }
              type="submit"
              title={quota - usage <= 0 ? t("No Quota") : t("Check Now")}
            />
          ) : (
            <Button
              onClick={onUpgradeToPremium}
              disabled={getUserLoading}
              type="button"
              title={t("Upgrade to Premium")}
            />
          )}
        </div>
      </div>
      <div className="mt-4 bg-white border border-gray-200 py-4 px-4 sm:px-6 rounded-md sm:rounded-lg shadow">
        <Partner
          title="Collaborating with Hexa Sarana Intermedia"
          description="Official PJAP Directorate General of Taxes"
          url="https://pajak.go.id/id/pt-hexa-sarana-intermedia"
        />
      </div>
    </div>
  );
}

export default NPWPValidate;
